// client/src/utils/resizeImage.js
//
// Shrink a photo in the browser before upload (Growth Phase 9 attendee
// photos): phone photos are often 4-12 MB; a 2048px JPEG is plenty for a
// recap post and uploads far faster on venue Wi-Fi. The server still
// normalises every image (externalUploadNormalizer.js), so this is a
// bandwidth saving, not a guarantee.
//
// Returns the original File unchanged when it is small already, isn't a
// still image the canvas can redraw (GIF keeps its animation), or the browser
// can't decode / encode it (old browsers, test environments).

export const MAX_DIMENSION = 2048;
export const SKIP_BELOW_BYTES = 600 * 1024;

const RESIZABLE = new Set(['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic', 'image/heif', 'image/bmp']);

/** Scale (w, h) to fit inside max × max, never upscaling. */
export function fitWithin(width, height, max = MAX_DIMENSION) {
  const w = Number(width) || 0;
  const h = Number(height) || 0;
  if (!w || !h) return { width: w, height: h };
  const scale = Math.min(1, max / Math.max(w, h));
  return { width: Math.round(w * scale), height: Math.round(h * scale) };
}

export const DECODE_TIMEOUT_MS = 15000;

function decode(file, timeoutMs) {
  const work = typeof createImageBitmap === 'function'
    // imageOrientation honours EXIF rotation where supported.
    ? createImageBitmap(file, { imageOrientation: 'from-image' })
    : new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
      img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
      img.src = url;
    });
  // A decoder that never answers must not hold the upload hostage.
  let timer;
  const timeout = new Promise((_, reject) => { timer = setTimeout(() => reject(new Error('decode timeout')), timeoutMs); });
  return Promise.race([work, timeout]).finally(() => clearTimeout(timer));
}

/**
 * @param {File} file
 * @param {object} [opts]
 * @param {number} [opts.maxDimension=2048]
 * @param {number} [opts.quality=0.85]
 * @param {number} [opts.decodeTimeoutMs=15000]
 * @returns {Promise<File>}
 */
export async function resizeImageFile(file, { maxDimension = MAX_DIMENSION, quality = 0.85, decodeTimeoutMs = DECODE_TIMEOUT_MS } = {}) {
  if (!file || !RESIZABLE.has(String(file.type).toLowerCase())) return file;
  if (file.size <= SKIP_BELOW_BYTES) return file;
  if (typeof document === 'undefined') return file;
  try {
    const source = await decode(file, decodeTimeoutMs);
    const { width, height } = fitWithin(source.width, source.height, maxDimension);
    if (!width || !height) return file;
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext && canvas.getContext('2d');
    if (!ctx || typeof canvas.toBlob !== 'function') return file;
    ctx.drawImage(source, 0, 0, width, height);
    if (typeof source.close === 'function') source.close();
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
    if (!blob || blob.size >= file.size) return file;
    const name = String(file.name || 'photo').replace(/\.[^.]+$/, '') + '.jpg';
    return new File([blob], name, { type: 'image/jpeg', lastModified: Date.now() });
  } catch {
    return file;
  }
}

/** Resize a list one at a time (keeps memory flat on phones). */
export async function resizeAll(files, opts) {
  const out = [];
  for (const f of files) out.push(await resizeImageFile(f, opts)); // eslint-disable-line no-await-in-loop
  return out;
}
